/** Bulk export dialog с field picker (Phase A3 MVP). */

import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";

const DEFAULT_FIELDS = ["id", "sku", "name_ru", "base_price", "sale_price", "is_active"];

export function ProductExportDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}): JSX.Element {
  const [selected, setSelected] = useState<Set<string>>(new Set(DEFAULT_FIELDS));
  const [downloading, setDownloading] = useState(false);

  const { data: fields, isLoading } = useQuery({
    queryKey: ["export-fields-products"],
    queryFn: async () => {
      const { data } = await api.get("/exports/products/fields");
      return data as Record<string, string>;
    },
    enabled: open,
  });

  useEffect(() => {
    if (!open) setSelected(new Set(DEFAULT_FIELDS));
  }, [open]);

  function toggle(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function download() {
    setDownloading(true);
    try {
      const fieldsParam = Array.from(selected).join(",");
      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/exports/products.csv?fields=${encodeURIComponent(fieldsParam)}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `products-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Экспорт готов");
      onOpenChange(false);
    } catch (e: any) {
      toast.error(`Не удалось экспортировать: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Экспорт товаров</DialogTitle>
          <DialogDescription>
            Выберите поля для CSV-экспорта. По умолчанию: ID, артикул, название
            (RU), цены, активность.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">
            Загрузка списка полей…
          </div>
        ) : (
          <div className="space-y-1.5 max-h-80 overflow-y-auto border rounded-md p-2">
            {Object.entries(fields ?? {}).map(([code, label]) => (
              <label
                key={code}
                className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 rounded cursor-pointer"
              >
                <Checkbox
                  checked={selected.has(code)}
                  onChange={() => toggle(code)}
                />
                <span className="flex-1 text-sm">{label}</span>
                <span className="text-xs font-mono text-muted-foreground">
                  {code}
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          Выбрано: {selected.size} из {Object.keys(fields ?? {}).length}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button
            onClick={download}
            disabled={selected.size === 0 || downloading}
          >
            <Download className="w-4 h-4 mr-1.5" />
            {downloading ? "Скачивание…" : "Скачать CSV"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
